// Vendor Imports
import $ from 'jquery';
import _, { get } from 'lodash';
import ReactDOM from 'react-dom';

// Project Imports
import { I18n } from 'common/i18n';

import BaseVisualization from './BaseVisualization/index';
import { type VisualizationOptions } from './BaseVisualization/types';
import MetadataProvider, { getFilterableColumns } from 'common/visualizations/dataProviders/MetadataProvider';
import { type ColumnAggregation } from 'common/visualizations/dataProviders/types';
import { type Vif, type OrderConfig, type CustomSortConfig } from '../vif';
import { type ColDef, type Column, type ColumnState, type IServerSideDatasource } from 'ag-grid-community';
import { type ViewColumn } from 'common/types/viewColumn';
import GridContainer from './agGridReact/GridContainer';
import { type Filters } from 'common/components/FilterBar/types';
import SoqlDataProvider from '../dataProviders/SoqlDataProvider';
import { type TableExportType, type ExportData, type UniqueValuesData } from './agGridReact/types';
import { type AgColumnFilter } from 'common/types/agGrid/filters';
import { tableActionsColumnDef } from 'common/visualizations/views/agGridReact/helpers/tableActionsColumnDef';
import {
  getDatasetUid,
  isCustomSortEnabled,
  isInlineDataSource,
  isTableActionFeatureEnabledForReports,
} from '../helpers/VifSelectors';
import { getTableActionsContext } from './agGridReact/helpers/tableActionsHelper';
import { type TableActionsConfigs } from './agGridReact/TableActionsButton/types';
import { type GrandTotalRow } from 'common/types/agGrid/grandTotal';

export interface AgTableViewOptions extends VisualizationOptions {
  locale?: string;
  handleColumnPivotChange: (columnState: ColumnState[], columns: Column[], hierarchyId?: string) => void;
  handleColumnPivotModeChange: (pivotMode: boolean, hierarchyId?: string) => void;
  handleColumnReorder: (columnState: ColumnState[]) => void;
  handleColumnResize: (columns: Column[] | null) => void;
  handleColumnRowGroupChange: (columnState: ColumnState[], columns: Column[], hierarchyId?: string) => void;
  handleColumnSort: (columnState: ColumnState[], hierarchyId?: string) => void;
  handleColumnValueChange: (columns: Column[], hierarchyId?: string) => void;
  handleColumnVisibilityChange: (
    columnState: ColumnState[],
    columns: Column[] | null,
    hierarchyId?: string,
  ) => void;
  handleGetGrandTotalRow: (hierarchyId: string) => Promise<GrandTotalRow>;
  handleHierarchyTabChange: (hierarchyId: string) => void;
  handleFilterChange: (newFilters: Filters, agFilterModel?: AgColumnFilter) => void;
  datasource: IServerSideDatasource;
  paginationPageSize?: number;
  defaultColDefOverrides?: ColDef;
  displayColumnFilters?: boolean;
  useSetFilters?: boolean;
  inVisualizationPreview?: boolean;
  getExportData?: (
    selectedFiltered: boolean,
    exportType: TableExportType,
    rowLimit?: number,
  ) => Promise<ExportData>;
  agFilterModel?: AgColumnFilter;
  getUniqueValues?: (
    columns: ViewColumn[],
    order: OrderConfig[],
    customSortConfig: CustomSortConfig | undefined,
    limit?: number,
  ) => Promise<UniqueValuesData>;
  handleColumnPinnedChange?: (columnName: string, pinned: boolean) => void;
  handleGroupColumnPinnedChange?: (colId: string, pinned: boolean, hierarchyId?: string) => void;
  handleCustomSortCycle: (newOrderConfig: OrderConfig[], hierarchyId?: string) => void;
  handleIndentedHeaderSortIndex?: (headerSortCycleIndex: number, hierarchyId?: string) => void;
}
export default class AgTableView extends BaseVisualization {
  locale?: string;
  $element: JQuery;
  vifToRender: Vif;
  columnMetadata: ViewColumn[];
  nonStandardAggregations: ColumnAggregation[] | null;

  superRenderError: (messages: string[]) => void;
  options: AgTableViewOptions;
  metadataProvider: MetadataProvider;
  soqlDataProvider: SoqlDataProvider;
  vizUid: string | undefined;
  componentUuid: string | undefined;

  constructor(element: JQuery, originalVif: Vif, options: AgTableViewOptions) {
    super(element, originalVif, options);

    this.$element = element;
    this.locale = _.get(options, 'locale');
    this.options = options;
    this.vifToRender = originalVif;
    this.columnMetadata = [];
    this.nonStandardAggregations = null;
    this.vizUid = this.$element.find('.socrata-visualization').attr('id');
    this.componentUuid = this.$element.parent().parent().parent().attr('data-component-uuid');

    // fix renderError since the way it is defined in BaseVisualization disallows normal method overrides
    this.superRenderError = this.renderError.bind(this);
    this.renderError = () => {
      this.$element.find('.socrata-visualization-container').removeClass('loaded');

      this.superRenderError(I18n.t('shared.visualizations.charts.table.error_unable_to_render', this.locale));
    };

    this.$element.find('.socrata-visualization').addClass('socrata-visualization-ag-grid');

    this.renderTemplate();
  }

  updateDataSource(newDataSource: IServerSideDatasource) {
    this.options.datasource = newDataSource;
  }

  shouldFetchAggregationSuggestions() {
    // When showAgGridColumnAggregations is not set, default to true
    return this.getOptions()?.showAgGridColumnAggregations ?? true;
  }

  async render(newVif: Vif): Promise<void> {
    if (!_.isEqual(newVif, this.vifToRender)) {
      this.vifToRender = newVif;

      this.updateVif(newVif);
      await this.updateColumnMetadata(newVif);
    }

    // https://socrata.atlassian.net/browse/EN-47840
    // Ensures the filters get rendered
    try {
      const isInline = isInlineDataSource(this.vifToRender);

      let datasetName: string | undefined;
      let tableActionsContext: TableActionsConfigs | undefined;
      let actionColumnDef: ColDef = {};
      let newTableActions: string[] = [];

      if (isInline) {
        this.clearError();
      } else {
        await this.updateFilterableColumns(newVif);
        const { metadata } = await this.getDatasetMetadata(newVif);
        datasetName = get(metadata, 'name');

        const vifColumns: ViewColumn[] = _.get(
          this.vifToRender,
          'series[0].dataSource.dimension.columns',
          [],
        );

        if (vifColumns.length && !this.columnMetadata.length) {
          await this.updateColumnMetadata(this.vifToRender);
        }

        // in the case that the vif references columns that no longer exist on the dataset, we will render an error
        if (vifColumns.some((col) => !this.columnMetadata.find((cm) => cm.fieldName === col.fieldName))) {
          this.renderGenericError(
            I18n.t('shared.visualizations.charts.table.error_missing_columns'),
            'datasetMissingColumn',
          );
          return;
        } else {
          this.clearError();
        }

        const isActionColumnFlagEnabled = isTableActionFeatureEnabledForReports();
        newTableActions = get(this.vifToRender, 'configuration.newTableActions', []);
        tableActionsContext = getTableActionsContext(metadata, this.vifToRender.configuration);
        const showTableActions = this.vifToRender.configuration.showProcessDiscoveryAction;
        if (showTableActions && isActionColumnFlagEnabled) {
          actionColumnDef = tableActionsColumnDef(tableActionsContext);
        }
      }

      const {
        datasource,
        handleColumnPivotChange,
        handleColumnPivotModeChange,
        handleColumnReorder,
        handleColumnResize,
        handleColumnRowGroupChange,
        handleColumnSort,
        handleColumnValueChange,
        handleColumnVisibilityChange,
        handleGetGrandTotalRow,
        handleHierarchyTabChange,
        paginationPageSize,
        defaultColDefOverrides,
        handleFilterChange,
        displayColumnFilters,
        useSetFilters,
        showAgGridColumnMenu,
        showAgGridColumnAggregations,
        inVisualizationPreview,
        getExportData,
        agFilterModel,
        getUniqueValues,
        handleColumnPinnedChange,
        handleGroupColumnPinnedChange,
        handleCustomSortCycle,
        handleIndentedHeaderSortIndex,
        setTableFilterInfo,
      } = this.options;

      const isSummaryTable = this.isSummaryTableVisualization();
      const isReadOnlyTable = isSummaryTable || this.options.lockColumnLayout === true;
      const showCustomSortMenuItems =
        isCustomSortEnabled() && !this.isInStory() && this.isAuthoringWorkflowActive() && !isReadOnlyTable;
      // AG Grid only offers its sort menu items from the kebab menu, not the legacy tabbed one.
      const useKebabColumnMenu = showCustomSortMenuItems || isReadOnlyTable;
      const showAgGridColumnMenuVal = showCustomSortMenuItems ? true : showAgGridColumnMenu;
      const agGridContainer = this.$element.find('.socrata-ag-table');
      ReactDOM.render(
        <GridContainer
          columnMetadata={this.columnMetadata}
          nonStandardAggregations={this.nonStandardAggregations}
          datasource={datasource}
          onHierarchyTabChange={handleHierarchyTabChange}
          getGrandTotalRow={handleGetGrandTotalRow}
          onColumnPivotChange={handleColumnPivotChange}
          onColumnPivotModeChange={handleColumnPivotModeChange}
          onColumnReorder={handleColumnReorder}
          onColumnResize={handleColumnResize}
          onColumnRowGroupChange={handleColumnRowGroupChange}
          onColumnSort={handleColumnSort}
          onColumnValueChange={handleColumnValueChange}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          vif={this.vifToRender}
          paginationPageSize={paginationPageSize}
          defaultColDefOverrides={defaultColDefOverrides}
          onFilterChange={handleFilterChange}
          displayColumnFilters={displayColumnFilters}
          useSetFilters={useSetFilters}
          agFilterModel={agFilterModel}
          showAgGridColumnMenu={showAgGridColumnMenuVal}
          showAgGridColumnAggregations={showAgGridColumnAggregations}
          openToolPanelByDefault={inVisualizationPreview}
          vizUid={this.vizUid}
          datasetName={datasetName}
          getExportData={getExportData}
          actionColumnDef={actionColumnDef}
          newTableActions={newTableActions}
          tableActionsContext={tableActionsContext}
          showCustomSortMenuItems={showCustomSortMenuItems}
          useKebabColumnMenu={useKebabColumnMenu}
          lockColumnLayout={isReadOnlyTable}
          getUniqueValues={getUniqueValues}
          handleColumnPinnedChange={handleColumnPinnedChange}
          handleGroupColumnPinnedChange={handleGroupColumnPinnedChange}
          handleCustomSortCycle={handleCustomSortCycle}
          handleIndentedHeaderSortIndex={handleIndentedHeaderSortIndex}
          componentUuid={this.componentUuid}
          setTableFilterInfo={setTableFilterInfo}
        />,
        agGridContainer[0],
      );
    } catch (e) {
      this.showVisualizationRenderError();
    }
  }

  renderCollocationMessage(): void {
    this.renderErrorMessage(I18n.t('shared.visualizations.charts.table.collocation_message', this.locale));
  }

  showVisualizationRenderError(): void {
    this.renderGenericError(
      I18n.t('shared.errors.private_or_deleted_asset.message'),
      'privateOrDeletedAsset',
    );
  }

  renderErrorMessage(message: string): void {
    this.$element.find('.socrata-visualization-container').removeClass('loaded');

    this.$container.find('.socrata-visualization-error-message').text(message);

    this.$container.removeClass('socrata-visualization-busy').addClass('socrata-visualization-error');
  }

  // If the 'small' class is added, the css will change in the agGridReact/index.css file
  renderComponentSize(width: number): void {
    if (width >= 450) {
      this.$element.find('.ag-grid-visualization').removeClass('small');
    } else {
      this.$element.find('.ag-grid-visualization').addClass('small');
    }
  }

  destroy(): void {
    this.$element.find('.socrata-ag-table').remove();
  }

  private renderTemplate(): void {
    const tableEl = $('<div>', { class: 'socrata-ag-table' });

    this.$element.find('.socrata-visualization-chart-container').append(tableEl);
  }

  async updateFilterableColumns(newVif: Vif): Promise<void> {
    const { metadata } = await this.getDatasetMetadata(newVif);
    // this call updates the columns in the base visualization,
    // which in turn updates the filter bar
    this.updateColumns(getFilterableColumns(metadata));
  }

  private async updateColumnMetadata(vif: Vif): Promise<void> {
    if (isInlineDataSource(vif)) {
      // Inline VIFs describe their own columns, and there is no dataset to ask about aggregations.
      this.columnMetadata = _.get(vif, 'series[0].dataSource.dimension.columns', []);
      this.nonStandardAggregations = null;
      return;
    }

    const { metadata, nonStandardAggregations } = await this.getDatasetMetadata(vif);
    this.columnMetadata = metadata.columns;
    this.nonStandardAggregations = nonStandardAggregations;
  }

  private async getDatasetMetadata(vif: Vif): Promise<any> {
    if (this.shouldInitializeProviders(getDatasetUid(vif))) {
      this.metadataProvider = MetadataProvider.fromVif(vif);
      this.soqlDataProvider = SoqlDataProvider.fromVif(vif);
    }

    return {
      metadata: await this.metadataProvider.getDatasetMetadata(),
      nonStandardAggregations: this.shouldFetchAggregationSuggestions()
        ? await this.metadataProvider.getAggregationSuggestions()
        : null,
    };
  }

  private shouldInitializeProviders(datasetUid: string) {
    if (!this.metadataProvider || !this.soqlDataProvider) {
      return true;
    }

    if (
      !_.isEqual(datasetUid, this.soqlDataProvider.getConfigurationProperty('datasetUid')) ||
      !_.isEqual(datasetUid, this.metadataProvider.getConfigurationProperty('datasetUid'))
    ) {
      return true;
    }

    return false;
  }
}
